import type { RosterGroupDto, RosterWorkingPattern } from '@my/directory-api';
import { SelectInput, TextInput, type SelectInputOption } from '@my/ui';
import { Loader2, X } from 'lucide-react';
import type { BulkRosterValues } from '../lib/bulkRoster';

const KEEP: SelectInputOption = { value: '', label: '— keep current —' };

interface BulkRosterBarProps {
  selectedCount: number;
  /** How many of the selected drivers aren't on the roster yet. */
  newCount: number;
  values: BulkRosterValues;
  groups: RosterGroupDto[];
  sites: { id: string; name: string }[];
  patternLabels: Record<RosterWorkingPattern, string>;
  progress: { done: number; total: number } | null;
  onChange: (patch: Partial<BulkRosterValues>) => void;
  onApply: () => void;
  onClear: () => void;
}

/**
 * Sticky bulk-action bar for the Drivers grid — appears once rows are selected.
 * Applies one set of roster settings to every selected driver, creating the
 * roster config for those that don't have one (which is what puts them on the
 * Roster grid).
 */
export function BulkRosterBar({
  selectedCount,
  newCount,
  values,
  groups,
  sites,
  patternLabels,
  progress,
  onChange,
  onApply,
  onClear,
}: BulkRosterBarProps) {
  const running = progress !== null;

  return (
    <div className="sticky top-0 z-10 space-y-3 rounded-md border border-purple-200 bg-purple-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-800">
          {selectedCount} driver{selectedCount === 1 ? '' : 's'} selected
        </span>
        {newCount > 0 && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
            {newCount} to add to the roster
          </span>
        )}
        {selectedCount - newCount > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
            {selectedCount - newCount} already on it — will be updated
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClear}
          disabled={running}
          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-200 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" /> Clear selection
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <SelectInput
          label="Pattern"
          name="bulk-pattern"
          value={values.workingPattern}
          disabled={running}
          options={[
            KEEP,
            ...(Object.keys(patternLabels) as RosterWorkingPattern[]).map((p) => ({
              value: p,
              label: patternLabels[p],
            })),
          ]}
          onChange={(e) =>
            onChange({ workingPattern: e.target.value as BulkRosterValues['workingPattern'] })
          }
        />
        <SelectInput
          label="Schedule"
          name="bulk-schedule"
          value={values.scheduleType}
          disabled={running}
          options={[
            KEEP,
            { value: 'FullTime', label: 'Full time' },
            { value: 'Casual', label: 'Casual' },
          ]}
          onChange={(e) =>
            onChange({ scheduleType: e.target.value as BulkRosterValues['scheduleType'] })
          }
        />
        <SelectInput
          label="Group"
          name="bulk-group"
          value={values.rosterGroupId}
          disabled={running}
          options={[KEEP, ...groups.map((g) => ({ value: g.id, label: g.name }))]}
          onChange={(e) => onChange({ rosterGroupId: e.target.value })}
        />
        <SelectInput
          label="Depot"
          name="bulk-depot"
          value={values.homeSiteId}
          disabled={running}
          options={[KEEP, ...sites.map((s) => ({ value: s.id, label: s.name }))]}
          onChange={(e) => onChange({ homeSiteId: e.target.value })}
        />
        <TextInput
          label="Start time"
          name="bulk-start-time"
          type="time"
          value={values.defaultStartTime}
          disabled={running}
          onChange={(e) => onChange({ defaultStartTime: e.target.value })}
        />
        <TextInput
          label="Start location"
          name="bulk-start-location"
          value={values.startLocation}
          disabled={running}
          placeholder="Keep current"
          onChange={(e) => onChange({ startLocation: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-purple-200 pt-3">
        <p className="text-[11px] italic text-slate-500">
          Blank fields keep each driver&apos;s current value — new drivers default to Full time ·
          Monday–Friday.
        </p>
        <div className="flex items-center gap-3">
          {running && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {progress.done}/{progress.total}
            </span>
          )}
          <button
            type="button"
            onClick={onApply}
            disabled={running || selectedCount === 0}
            className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply to {selectedCount} driver{selectedCount === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
